import type {
  Dapp,
  StoreListingDraft,
  StoreListingDraftValues,
} from '@src/dto';

/**
 * Store Listing drafts ("Save Draft") live on the dapp record
 * (`Dapp.storeListingDraft`, written via `saveStoreListingDraft`) until the
 * publisher hits "Publish Updates" or discards them.
 */
export type { StoreListingDraft, StoreListingDraftValues };

type DraftComparableDapp = Pick<
  Dapp,
  | 'dappName'
  | 'subtitle'
  | 'description'
  | 'languages'
  | 'availableCountriesMode'
  | 'availableCountries'
  | 'appWebsite'
  | 'contactEmail'
  | 'supportEmail'
  | 'licenseUrl'
  | 'privacyPolicyUrl'
  | 'editorsChoiceHeadline'
  | 'editorsChoiceTag'
  | 'dappIconUrl'
  | 'bannerUrl'
  | 'dappPreviewUrls'
  | 'editorsChoiceGraphicUrl'
  | 'releaseApprovalMode'
>;

const normalize = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }
  if (value === 'all') {
    return 'include';
  }
  return String(value);
};

/** Number of draft fields that differ from the saved dapp record. */
export function countDraftChanges(
  draft: StoreListingDraft | null,
  dapp: DraftComparableDapp | null | undefined,
): number {
  if (!draft) {
    return 0;
  }
  if (!dapp) {
    return Object.keys(draft.values).length;
  }

  return (
    Object.keys(draft.values) as (keyof StoreListingDraftValues)[]
  ).filter((key) => normalize(draft.values[key]) !== normalize(dapp[key]))
    .length;
}
